import { PostData } from "./FetchData";
import { formatAuditIdForApi, isValidAuditId } from "../utils/auditIdUtils";

const MS1 = "ms1";

export const WRITING_JOB_POLL_MS = 2500;

function extractMs1Message(payload, fallbackMessage) {
	if (typeof payload === "string" && payload.trim()) return payload.trim();
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message.trim();
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message.trim();
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail.trim();
	}
	if (Array.isArray(payload?.detail) && payload.detail.length) {
		const parts = payload.detail
			.map((d) =>
				typeof d === "string"
					? d
					: typeof d?.msg === "string"
						? d.msg
						: null
			)
			.filter(Boolean);
		if (parts.length) return parts.join("; ");
	}
	if (payload?.data && typeof payload.data.detail === "string") {
		return payload.data.detail.trim();
	}
	return fallbackMessage;
}

function parseMs1Response(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "failure") {
		return {
			ok: false,
			message: extractMs1Message(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res?.status !== "success") {
		return {
			ok: false,
			message: extractMs1Message(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	return { ok: true, message: res.message ?? null, data: res.data ?? null };
}

export function isWritingJobComplete(status) {
	const s = String(status || "").toLowerCase();
	return s === "completed";
}

export function isWritingJobFailed(status, errorMessage) {
	const s = String(status || "").toLowerCase();
	if (s === "failed" || s === "error") return true;
	return errorMessage != null && errorMessage !== "";
}

export async function getWritingAssistantJobPreview(websiteId, jobId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantJobPreview", {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
	});
	const parsed = parseMs1Response(res, "Failed to load job preview");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function getWritingAssistantJobStatus(websiteId, jobId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantJobStatus", {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
	});
	const parsed = parseMs1Response(res, "Failed to load job status");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function regenerateWritingAssistantJob(websiteId, jobId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantRegenerate", {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
	});
	const parsed = parseMs1Response(res, "Failed to regenerate job");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function getWritingAssistantRecommendations(websiteId, auditId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", recommendations: [] };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", recommendations: [] };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantRecommendations", {
		websiteId: Number(websiteId),
		auditId: formatAuditIdForApi(auditId),
	});
	const parsed = parseMs1Response(res, "Failed to load recommendations");
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, recommendations: [] };
	}
	const recommendations = Array.isArray(parsed.data?.recommendations)
		? parsed.data.recommendations
		: [];
	return { ok: true, message: parsed.message, recommendations };
}

export async function listWritingAssistantRecommendationsList(
	websiteId,
	options = {}
) {
	const {
		skip = 0,
		limit = 20,
		title,
		category,
		severity,
		impact,
		jobStatus,
		url,
		auditId,
		sortDir = "desc",
	} = options;
	const empty = {
		ok: false,
		message: "Missing website id",
		items: [],
		total: 0,
		skip: 0,
		limit: 20,
		hasMore: false,
		integrationAvailable: false,
	};
	if (websiteId == null) {
		return empty;
	}
	const safeLimit = Math.min(100, Math.max(1, Number(limit) || 20));
	const safeSkip = Math.max(0, Number(skip) || 0);
	const body = {
		websiteId: Number(websiteId),
		skip: safeSkip,
		limit: safeLimit,
	};
	const titleVal = String(title || "").trim();
	if (titleVal) body.title = titleVal;
	if (category) body.category = category;
	// Empty string is valid (UI "None" severity)
	if (Object.prototype.hasOwnProperty.call(options, "severity")) {
		body.severity = severity == null ? "" : String(severity);
	}
	if (impact) body.impact = impact;
	if (jobStatus) body.jobStatus = jobStatus;
	// Empty string is valid (UI "Site-wide" / null url)
	if (Object.prototype.hasOwnProperty.call(options, "url")) {
		body.url = url == null ? "" : String(url);
	}
	if (auditId != null && isValidAuditId(auditId)) {
		body.auditId = formatAuditIdForApi(auditId);
	}
	if (sortDir) body.sortDir = sortDir;

	const res = await PostData(
		MS1,
		"api/v1/website/writingAssistantRecommendationsList",
		body
	);
	const parsed = parseMs1Response(res, "Failed to load recommendations list");
	if (!parsed.ok) {
		return {
			...empty,
			ok: false,
			message: parsed.message,
			skip: safeSkip,
			limit: safeLimit,
		};
	}
	const data = parsed.data || {};
	const items = Array.isArray(data.items) ? data.items : [];
	return {
		ok: true,
		message: parsed.message,
		items,
		total: Number(data.total) || 0,
		skip: Number(data.skip) ?? safeSkip,
		limit: Number(data.limit) || safeLimit,
		hasMore: Boolean(data.has_more),
		integrationAvailable: Boolean(data.integration_available),
	};
}

export async function submitWritingAssistantJob(
	websiteId,
	{ auditId, recommendationId, url }
) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", job: null };
	}
	if (recommendationId == null || recommendationId === "") {
		return { ok: false, message: "Missing recommendation id", job: null };
	}
	if (!url) {
		return { ok: false, message: "Missing page url", job: null };
	}
	const formattedRecId = Number.isFinite(Number(recommendationId))
		? Number(recommendationId)
		: String(recommendationId);
	const res = await PostData(MS1, "api/v1/website/writingAssistantSubmitJob", {
		websiteId: Number(websiteId),
		auditId: formatAuditIdForApi(auditId),
		recommendationId: formattedRecId,
		url,
	});
	const parsed = parseMs1Response(res, "Failed to submit job");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function listWritingAssistantJobs(
	websiteId,
	{ skip = 0, limit = 20, auditId } = {}
) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", jobs: [], total: 0, hasMore: false };
	}
	const body = {
		websiteId: Number(websiteId),
		skip,
		limit,
	};
	if (auditId != null && isValidAuditId(auditId)) {
		body.auditId = formatAuditIdForApi(auditId);
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantJobs", body);
	const parsed = parseMs1Response(res, "Failed to load jobs");
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, jobs: [], total: 0, hasMore: false };
	}
	const jobs = Array.isArray(parsed.data?.jobs) ? parsed.data.jobs : [];
	const total =
		parsed.data?.total != null ? Number(parsed.data.total) : jobs.length;
	return {
		ok: true,
		message: parsed.message,
		jobs,
		total,
		hasMore: Boolean(parsed.data?.has_more) || skip + jobs.length < total,
	};
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function pollWritingAssistantJob(
	websiteId,
	jobId,
	{ intervalMs = WRITING_JOB_POLL_MS, onTick } = {}
) {
	if (websiteId == null || jobId == null || jobId === "") {
		return { ok: false, message: "Missing website or job id", job: null };
	}

	for (;;) {
		const result = await getWritingAssistantJobStatus(websiteId, jobId);
		if (!result.ok) {
			return { ok: false, message: result.message, job: null };
		}

		const job = result.job;
		if (typeof onTick === "function") onTick(job);

		if (isWritingJobFailed(job?.status, job?.error_message)) {
			return {
				ok: false,
				message: job?.error_message || "Job failed",
				job,
			};
		}

		if (isWritingJobComplete(job?.status)) {
			return { ok: true, message: null, job };
		}

		await sleep(intervalMs);
	}
}

export async function listWritingAssistantJobValidations(
	websiteId,
	jobId,
	{ skip = 0, limit = 20 } = {},
  ) {
	if (websiteId == null) {
	  return {
		ok: false,
		message: "Missing website id",
		validations: [],
		total: 0,
		hasMore: false,
	  };
	}
	if (jobId == null || jobId === "") {
	  return {
		ok: false,
		message: "Missing job id",
		validations: [],
		total: 0,
		hasMore: false,
	  };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantJobValidations", {
	  websiteId: Number(websiteId),
	  jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
	  skip,
	  limit,
	});
	const parsed = parseMs1Response(res, "Failed to load job validations");
	if (!parsed.ok) {
	  return {
		ok: false,
		message: parsed.message,
		validations: [],
		total: 0,
		hasMore: false,
	  };
	}
	const validations = Array.isArray(parsed.data?.validations)
	  ? parsed.data.validations
	  : [];
	const total =
	  parsed.data?.total != null ? Number(parsed.data.total) : validations.length;
	const hasMore = Boolean(parsed.data?.has_more);
	return { ok: true, message: parsed.message, validations, total, hasMore };
  }
  
  export async function submitWritingAssistantJobValidation(websiteId, jobId) {
	if (websiteId == null) {
	  return { ok: false, message: "Missing website id", validation: null };
	}
	if (jobId == null || jobId === "") {
	  return { ok: false, message: "Missing job id", validation: null };
	}
	const res = await PostData(
	  MS1,
	  "api/v1/website/writingAssistantSubmitJobValidation",
	  {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
	  },
	);
	const parsed = parseMs1Response(res, "Failed to submit job validation");
	if (!parsed.ok) {
	  return { ok: false, message: parsed.message, validation: null };
	}
	return { ok: true, message: parsed.message, validation: parsed.data };
  }
  
  export async function getWritingAssistantJobValidationStatus(
	  websiteId,
	  jobId,
	  validationId
  ) {
	  if (websiteId == null) {
		  return { ok: false, message: "Missing website id", validation: null };
	  }
	  if (jobId == null || jobId === "") {
		  return { ok: false, message: "Missing job id", validation: null };
	  }
	  if (validationId == null || validationId === "") {
		  return { ok: false, message: "Missing validation id", validation: null };
	  }
	  const res = await PostData(MS1, "api/v1/website/writingAssistantJobValidationStatus", {
		  websiteId: Number(websiteId),
		  jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
		  validationId: Number.isFinite(Number(validationId))
			  ? Number(validationId)
			  : validationId,
	  });
	  const parsed = parseMs1Response(res, "Failed to load validation status");
	  if (!parsed.ok) {
		  return { ok: false, message: parsed.message, validation: null };
	  }
	  return { ok: true, message: parsed.message, validation: parsed.data };
  }
  
  export function isValidationPending(status) {
	  const s = String(status || "").toLowerCase();
	  return (
		  s === "pending" ||
		  s === "processing" ||
		  s === "running" ||
		  s === "queued" ||
		  s === "in_progress"
	  );
  }
  
  export function isValidationTerminal(status) {
	  const s = String(status || "").toLowerCase();
	  return s === "completed" || s === "failed" || s === "error";
  }
  
  export function getLatestValidation(validations) {
	  if (!Array.isArray(validations) || validations.length === 0) return null;
	  return validations[0];
  }
  
  export function mergeValidationIntoList(validations, validation) {
	  if (!validation?.id) return Array.isArray(validations) ? validations : [];
	  const list = Array.isArray(validations) ? validations : [];
	  const idx = list.findIndex((v) => v?.id === validation.id);
	  if (idx >= 0) {
		  const next = [...list];
		  next[idx] = { ...next[idx], ...validation };
		  return next;
	  }
	  return [validation, ...list];
  }
  
  export function countFixedChanges(validation) {
	  if (!validation || !Array.isArray(validation.changes)) return 0;
	  return validation.changes.filter((item) => {
		  if (item == null) return false;
		  if (item.fixed === true || item.verified === true || item.applied === true) {
			  return true;
		  }
		  const status = String(item.status || item.result_status || "").toLowerCase();
		  return (
			  status === "fixed" ||
			  status === "verified" ||
			  status === "applied" ||
			  status === "success" ||
			  status === "passed"
		  );
	  }).length;
  }
  
  export async function pollWritingAssistantJobValidations(
	  websiteId,
	  jobId,
	  validationId,
	  { intervalMs = WRITING_JOB_POLL_MS, onTick } = {}
  ) {
	  if (websiteId == null || jobId == null || jobId === "") {
		  return { ok: false, message: "Missing website or job id", validation: null };
	  }
	  if (validationId == null || validationId === "") {
		  return { ok: false, message: "Missing validation id", validation: null };
	  }
  
	  for (;;) {
		  const result = await getWritingAssistantJobValidationStatus(
			  websiteId,
			  jobId,
			  validationId
		  );
		  if (!result.ok) {
			  return { ok: false, message: result.message, validation: null };
		  }
  
		  const validation = result.validation;
		  if (typeof onTick === "function") onTick(validation);
  
		  if (!validation) {
			  return { ok: true, message: null, validation: null };
		  }
  
		  const status = validation.status;
		  if (isValidationTerminal(status)) {
			  const s = String(status || "").toLowerCase();
			  if (s === "failed" || s === "error") {
				  return {
					  ok: false,
					  message: validation.error_message || validation.message || "Validation failed",
					  validation,
				  };
			  }
			  return { ok: true, message: null, validation };
		  }
  
		  await sleep(intervalMs);
	  }
  }

/**
 * Call writingAssistantSavePreviewEdits to persist user-edited blocks back to
 * the preview pipeline. Returns the validated envelope including allowed flag,
 * optional rejection explanation, and (on success) the refreshed preview.
 *
 * @param {string|number} websiteId
 * @param {string|number} jobId
 * @param {Array<{ change_index: number, edited_content: string }>} edits
 */
export async function savePreviewEdits(websiteId, jobId, edits) {
	if (websiteId == null) {
		return {
			ok: false,
			message: "Missing website id",
			allowed: false,
			explanation: null,
			preview: null,
		};
	}
	if (jobId == null || jobId === "") {
		return {
			ok: false,
			message: "Missing job id",
			allowed: false,
			explanation: null,
			preview: null,
		};
	}
	if (!Array.isArray(edits) || edits.length === 0) {
		return {
			ok: false,
			message: "No edits to save",
			allowed: false,
			explanation: null,
			preview: null,
		};
	}
	const safeEdits = edits
		.filter((e) => e != null)
		.map((e) => ({
			change_index: Number(e.change_index),
			edited_content: String(e.edited_content || ""),
		}));
	if (safeEdits.length === 0) {
		return {
			ok: false,
			message: "No edits to save",
			allowed: false,
			explanation: null,
			preview: null,
		};
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantSavePreviewEdits", {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
		edits: safeEdits,
	});
	const parsed = parseMs1Response(res, "Failed to save preview edits");
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			allowed: false,
			explanation: null,
			preview: null,
		};
	}
	const data = parsed.data || {};
	return {
		ok: true,
		message: parsed.message,
		allowed: Boolean(data.allowed),
		explanation: data.explanation ?? null,
		preview: data.preview ?? null,
	};
}

/**
 * WordPress deploy job id lives in response_body (not top-level job_id,
 * which is the writing assistant job).
 */
function parseExternalDeployWpJobId(data) {
	const raw = data?.response_body ?? data?.responseBody;
	if (typeof raw === "string" && raw.trim()) {
		try {
			const body = JSON.parse(raw);
			const nested = body?.job_id ?? body?.jobId ?? null;
			if (nested != null && nested !== "") return nested;
		} catch {
			// fall through
		}
	} else if (raw && typeof raw === "object") {
		const nested = raw.job_id ?? raw.jobId ?? null;
		if (nested != null && nested !== "") return nested;
	}
	return null;
}

/**
 * Deploy all changes for a recommendation (WordPress external deploy).
 * POST writingAssistantExternalDeploy with scope "recommendation".
 */
export async function externalDeployWritingAssistant({
	websiteId,
	jobId,
	recommendationId,
	scope = "recommendation",
}) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", jobId: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", jobId: null };
	}
	if (recommendationId == null || recommendationId === "") {
		return { ok: false, message: "Missing recommendation id", jobId: null };
	}
	const res = await PostData(MS1, "api/v1/website/writingAssistantExternalDeploy", {
		websiteId: Number(websiteId),
		jobId: Number.isFinite(Number(jobId)) ? Number(jobId) : jobId,
		scope: scope || "recommendation",
		recommendationId: Number.isFinite(Number(recommendationId))
			? Number(recommendationId)
			: recommendationId,
	});
	const parsed = parseMs1Response(res, "Failed to start deploy");
	if (!parsed.ok) return { ok: false, message: parsed.message, jobId: null };
	const data = parsed.data || {};
	const deployJobId = parseExternalDeployWpJobId(data);
	if (deployJobId == null) {
		return {
			ok: false,
			message:
				parsed.message ||
				"Deploy started but WordPress job id was missing from response",
			jobId: null,
			data,
		};
	}
	return {
		ok: true,
		message: parsed.message || data.message,
		jobId: deployJobId,
		data,
	};
}